"use client";

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ActivitySessionFeedCursor,
  type ActivitySessionFeedItem,
  type ActivitySessionFeedResponse,
  type CreateActivitySessionInput,
  createActivitySession,
  getActivitySessionById,
  getActivitySessionsFeed,
  joinActivitySession,
  leaveActivitySession,
  updateParticipantTeam,
} from "@/actions/activity-session";
import type { ActivitySessionFormat } from "@/db/schema/activity-session";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export interface ActivitySessionParticipant {
  id: string;
  displayName: string;
  username: string | null;
  avatarUrl: string | null;
  role: string;
  team: "team1" | "team2" | null;
}

export interface ActivitySessionDetail extends ActivitySessionFeedItem {
  inviteCode: string | null;
  chatId: number | null;
}

// ──────────────────────────────────────────────────────────────────────────────
// QUERY: Activity Session Details
// ──────────────────────────────────────────────────────────────────────────────

export function useActivitySession(activitySessionId: number | undefined) {
  return useQuery({
    queryKey: ["activitySession", activitySessionId],
    queryFn: async () => {
      if (!activitySessionId) return null;
      const result = await getActivitySessionById(activitySessionId);
      if (!result.ok) {
        throw new Error(result.message);
      }
      return result.data as ActivitySessionDetail;
    },
    staleTime: 30_000, // treat data as fresh for 30s
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled: !!activitySessionId,
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// MUTATION: Update Participant Team
// ──────────────────────────────────────────────────────────────────────────────

export function useUpdateParticipantTeam(activitySessionId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateParticipantTeam", activitySessionId],
    mutationFn: ({
      userId,
      team,
    }: {
      userId: string;
      team: "team1" | "team2";
    }) =>
      updateParticipantTeam({
        activitySessionId,
        userId,
        team,
      }),

    onMutate: async ({ userId, team }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["activitySession", activitySessionId],
      });

      // Snapshot the previous value
      const prev = queryClient.getQueryData<ActivitySessionDetail>([
        "activitySession",
        activitySessionId,
      ]);

      // Optimistically update
      queryClient.setQueryData<ActivitySessionDetail>(
        ["activitySession", activitySessionId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            participants: old.participants.map((p) =>
              p.id === userId ? { ...p, team } : p,
            ),
          };
        },
      );

      return { prev };
    },

    onError: (_err, _vars, ctx) => {
      // Rollback to previous state
      if (ctx?.prev) {
        queryClient.setQueryData(
          ["activitySession", activitySessionId],
          ctx.prev,
        );
      }
    },

    onSettled: () => {
      // Always refetch after mutation
      queryClient.invalidateQueries({
        queryKey: ["activitySession", activitySessionId],
      });
    },
  });
}

export const useUpdateActivitySessionParticipantTeam = useUpdateParticipantTeam;

// ──────────────────────────────────────────────────────────────────────────────
// MUTATION: Join Activity Session
// ──────────────────────────────────────────────────────────────────────────────

export function useJoinActivitySession(
  activitySessionId: number,
  currentUser: {
    id: string;
    displayName: string;
    username: string | null;
    avatarUrl: string | null;
  },
  sessionFormat: ActivitySessionFormat | null,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["joinActivitySession", activitySessionId],
    mutationFn: (team?: "team1" | "team2") =>
      joinActivitySession(activitySessionId, team),

    onMutate: async (team) => {
      await queryClient.cancelQueries({
        queryKey: ["activitySession", activitySessionId],
      });

      const prev = queryClient.getQueryData<ActivitySessionDetail>([
        "activitySession",
        activitySessionId,
      ]);

      // Determine team assignment based on game format and current participants
      let assignedTeam = team;
      if (
        !assignedTeam &&
        (sessionFormat === "singles" ||
          sessionFormat === "doubles" ||
          sessionFormat === "team") &&
        prev
      ) {
        // Auto-assign to team with fewer members
        const yourTeamCount = prev.participants.filter(
          (p) => p.team === "team1",
        ).length;
        const opponentTeamCount = prev.participants.filter(
          (p) => p.team === "team2",
        ).length;

        assignedTeam = yourTeamCount <= opponentTeamCount ? "team1" : "team2";
      }

      // Optimistically add current user as participant
      queryClient.setQueryData<ActivitySessionDetail>(
        ["activitySession", activitySessionId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            participantCount: old.participantCount + 1,
            isJoinedByViewer: true,
            participants: [
              ...old.participants,
              {
                id: currentUser.id,
                displayName: currentUser.displayName,
                username: currentUser.username,
                avatarUrl: currentUser.avatarUrl,
                role: "participant",
                team: assignedTeam ?? null,
              },
            ],
          };
        },
      );

      return { prev, assignedTeam };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ["activitySession", activitySessionId],
          ctx.prev,
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["activitySession", activitySessionId],
      });
      queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
    },
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// MUTATION: Leave Activity Session
// ──────────────────────────────────────────────────────────────────────────────

export function useLeaveActivitySession(
  activitySessionId: number,
  currentUserId: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["leaveActivitySession", activitySessionId],
    mutationFn: () => leaveActivitySession(activitySessionId),

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ["activitySession", activitySessionId],
      });

      const prev = queryClient.getQueryData<ActivitySessionDetail>([
        "activitySession",
        activitySessionId,
      ]);

      // Optimistically remove current user
      queryClient.setQueryData<ActivitySessionDetail>(
        ["activitySession", activitySessionId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            participantCount: Math.max(0, old.participantCount - 1),
            isJoinedByViewer: false,
            participants: old.participants.filter(
              (p) => p.id !== currentUserId,
            ),
          };
        },
      );

      return { prev };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ["activitySession", activitySessionId],
          ctx.prev,
        );
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["activitySession", activitySessionId],
      });
      queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
    },
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// HELPER: Get participants grouped by team
// ──────────────────────────────────────────────────────────────────────────────

export function useActivitySessionTeams(
  activitySession: ActivitySessionDetail | null | undefined,
) {
  if (!activitySession) {
    return {
      yourTeam: [],
      opponentTeam: [],
      unassigned: [],
    };
  }

  const yourTeam = activitySession.participants.filter(
    (p) => p.team === "team1",
  );
  const opponentTeam = activitySession.participants.filter(
    (p) => p.team === "team2",
  );
  const unassigned = activitySession.participants.filter((p) => !p.team);

  return { yourTeam, opponentTeam, unassigned };
}

// ──────────────────────────────────────────────────────────────────────────────
// QUERY: Activity Sessions Feed
// ──────────────────────────────────────────────────────────────────────────────

export function useActivitySessionFeed(
  limit: number,
  initialEvents?: ActivitySessionFeedItem[],
) {
  const initialData = initialEvents
    ? {
        pages: [
          {
            events: initialEvents,
            hasMore: false,
            nextCursor: null,
          } as ActivitySessionFeedResponse,
        ],
        pageParams: [null],
      }
    : undefined;

  return useInfiniteQuery({
    queryKey: ["activitySessions", "feed", limit],
    queryFn: async ({ pageParam }) => {
      const result = await getActivitySessionsFeed({
        cursor: pageParam ?? undefined,
        limit,
        includeCompleted: true,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch activity sessions");
      }

      return result.data;
    },
    initialPageParam: null as ActivitySessionFeedCursor | null,
    initialData,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// MUTATION: Create Activity Session
// ──────────────────────────────────────────────────────────────────────────────

export function useCreateActivitySessionMutation(
  currentUser: {
    id: string;
    displayName: string;
    avatarUrl?: string | null;
  },
  limit: number = 20,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createActivitySession"],
    mutationFn: async (input: CreateActivitySessionInput) => {
      const result = await createActivitySession(input);
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create activity session");
      }
      return { id: result.data.id, input };
    },
    onMutate: async (input) => {
      const queryKey = ["activitySessions", "feed", limit];
      await queryClient.cancelQueries({ queryKey });

      const prev =
        queryClient.getQueryData<InfiniteData<ActivitySessionFeedResponse>>(
          queryKey,
        );

      // Optimistic update
      const organizerPlaying =
        input.split?.organizerPlaying ??
        (input.split?.pricing === "divide_total" || input.split == null);
      const optimisticEvent: ActivitySessionFeedItem = {
        id: -Date.now(), // temp ID
        activityId: input.activityId,
        // Since we don't have the full object, we mock what we can
        // Note: Ideally we'd map activityId to name/icon here if available
        activityName: "Activity",
        activityIcon: "activity", // fallback
        title: input.title,
        description: input.description,
        entryType: "session",
        status: "upcoming",
        cancelReason: null,
        scoreVerificationStatus: null,
        scoreSummary: null,
        visibility: input.visibility,
        requiresApproval: Boolean(input.requiresApproval),
        createdAt: new Date(),
        startTime: input.startTime,
        endTime: input.endTime,
        locationName: input.locationName,
        address: input.address,
        latitude: input.latitude,
        longitude: input.longitude,
        pricingType: input.pricingType,
        pricePerPersonAmountMinor: input.pricePerPersonAmountMinor,
        splitPricing: input.split?.pricing ?? null,
        currency: input.currency,
        minSkillLevel: input.minSkillLevel ?? null,
        maxSkillLevel: input.maxSkillLevel ?? null,
        viewerSkillLevel: null,
        isViewerSkillLevelEligible: null,
        scoringSchema: null,
        minParticipants: input.minParticipants ?? 1,
        maxParticipants: input.maxParticipants ?? 10,
        participantCount: organizerPlaying ? 1 : 0,
        competitionMode: input.competitionMode ?? "casual",
        sessionFormat: input.sessionFormat ?? null,
        creator: {
          id: currentUser.id,
          displayName: currentUser.displayName,
          username: null,
          avatarUrl: currentUser.avatarUrl ?? null,
        },
        participants: organizerPlaying
          ? [
              {
                id: currentUser.id,
                displayName: currentUser.displayName,
                username: null,
                avatarUrl: currentUser.avatarUrl ?? null,
                role: "creator",
                team:
                  (input.sessionFormat && input.sessionFormat !== "group") ||
                  (input.sessionFormat && input.sessionFormat !== "group")
                    ? "team1"
                    : null,
              },
            ]
          : [],
        likeCount: 0,
        commentCount: 0,
        shareCount: 0,
        isLikedByViewer: false,
        isJoinedByViewer: true,
        isCreatedByViewer: true,
        viewerJoinRequest: null,
      };

      queryClient.setQueryData<InfiniteData<ActivitySessionFeedResponse>>(
        queryKey,
        (old) => {
          if (!old?.pages.length) return old;
          const [first, ...rest] = old.pages;
          return {
            ...old,
            pages: [
              {
                ...first,
                events: [optimisticEvent, ...first.events],
              },
              ...rest,
            ],
          };
        },
      );

      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(["activitySessions", "feed", limit], ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["activitySessions", "feed", limit],
      });
    },
  });
}
